/**
 * case-refs-http — live implementation of `CaseRefsPort`.
 *
 * Wraps `GET /api/v1/cases/{caseId}/refs` (BE `services/case_refs/`).
 * The endpoint serialises **camelCase** (Pydantic `to_camel` aliases,
 * like `/citation-registry/*`), so this adapter reads camelCase keys
 * directly — unlike `case-http.ts` which reads the snake_case `/cases`
 * + `/case-ecritures` wires.
 *
 * Hand-rolled validation (no zod / no codegen), per the auth-http /
 * case-http precedent. W1/W2: pure field projection, no business logic,
 * no Office.js. The BE owns every ordinal/token here.
 */

import type { CaseRefsPort } from '@/domain/case-refs/ports'
import type {
  BordereauValidation,
  CaseRefDirectory,
  FactRef,
  MoyenRef,
  PieceRef,
  PieceRefStatus,
  RefParty,
  RefStatusKind,
  UncitedPromoted,
} from '@/domain/case-refs/entity'
import type { HttpErrorKind } from './_errors'

export interface CaseRefsHttpConfig {
  baseUrl: string
  getAccessToken: () => string | null
  fetch?: typeof fetch
}

class CaseRefsHttpError extends Error {
  constructor(
    public readonly kind: HttpErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'CaseRefsHttpError'
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function readNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function readBoolean(v: unknown): boolean {
  return v === true
}

function readParty(v: unknown): RefParty {
  return v === 'adverse' || v === 'joint' ? v : 'mine'
}

function readArray(raw: unknown, key: string): unknown[] {
  if (isPlainObject(raw) && Array.isArray(raw[key])) return raw[key]
  return []
}

function mapPiece(raw: unknown): PieceRef {
  const w = isPlainObject(raw) ? raw : {}
  return {
    refId: readString(w.refId),
    ordinal: readNumber(w.ordinal),
    party: readParty(w.party),
    token: readString(w.token),
    label: readString(w.label),
    isPromoted: readBoolean(w.isPromoted),
    communicationStatus: readString(w.communicationStatus),
  }
}

function mapFact(raw: unknown): FactRef {
  const w = isPlainObject(raw) ? raw : {}
  return {
    refId: readString(w.refId),
    ordinal: readNumber(w.ordinal),
    token: readString(w.token),
    text: readString(w.text),
  }
}

function mapMoyen(raw: unknown): MoyenRef {
  const w = isPlainObject(raw) ? raw : {}
  return {
    refId: readString(w.refId),
    ordinal: readNumber(w.ordinal),
    token: readString(w.token),
    title: readString(w.title),
  }
}

function readNullableString(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

function readNullableNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function readNullableParty(v: unknown): RefParty | null {
  return v === 'mine' || v === 'adverse' || v === 'joint' ? v : null
}

function readRefStatusKind(v: unknown): RefStatusKind {
  return v === 'not_promoted' || v === 'orphan' ? v : 'ok'
}

function mapStatus(raw: unknown): PieceRefStatus {
  const w = isPlainObject(raw) ? raw : {}
  return {
    refId: readString(w.refId),
    status: readRefStatusKind(w.status),
    token: readNullableString(w.token),
    ordinal: readNullableNumber(w.ordinal),
    party: readNullableParty(w.party),
    label: readNullableString(w.label),
  }
}

function mapUncited(raw: unknown): UncitedPromoted {
  const w = isPlainObject(raw) ? raw : {}
  return {
    refId: readString(w.refId),
    token: readString(w.token),
    ordinal: readNumber(w.ordinal),
    party: readParty(w.party),
    label: readString(w.label),
  }
}

export function createCaseRefsHttp(config: CaseRefsHttpConfig): CaseRefsPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))

  async function request(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
  ): Promise<unknown> {
    const token = config.getAccessToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    let res: Response
    try {
      res = await doFetch(`${config.baseUrl}${path}`, {
        method,
        headers,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })
    } catch (err) {
      throw new CaseRefsHttpError(
        'network',
        err instanceof Error ? err.message : 'Réseau injoignable',
      )
    }
    if (res.status === 401) {
      throw new CaseRefsHttpError('auth', 'Session expirée — reconnectez-vous')
    }
    if (res.status === 429) {
      throw new CaseRefsHttpError('rate_limited', 'Trop de requêtes — patientez')
    }
    if (!res.ok) {
      throw new CaseRefsHttpError(
        'server',
        `Requête échouée (HTTP ${res.status})`,
      )
    }
    try {
      return await res.json()
    } catch (err) {
      throw new CaseRefsHttpError(
        'malformed',
        err instanceof Error ? `JSON invalide: ${err.message}` : 'JSON invalide',
      )
    }
  }

  return {
    async resolve(caseId: string): Promise<CaseRefDirectory> {
      const raw = await request(
        'GET',
        `/api/v1/cases/${encodeURIComponent(caseId)}/refs`,
      )
      return {
        pieces: readArray(raw, 'pieces').map(mapPiece),
        facts: readArray(raw, 'facts').map(mapFact),
        moyens: readArray(raw, 'moyens').map(mapMoyen),
      }
    },

    async validate(
      caseId: string,
      refIds: string[],
    ): Promise<BordereauValidation> {
      const raw = await request(
        'POST',
        `/api/v1/cases/${encodeURIComponent(caseId)}/refs/validate`,
        { refIds },
      )
      return {
        statuses: readArray(raw, 'statuses').map(mapStatus),
        uncitedPromoted: readArray(raw, 'uncitedPromoted').map(mapUncited),
      }
    },
  }
}

export { CaseRefsHttpError }
